import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, catchError, map, of } from "rxjs";
import { environment } from "@env";
import { KeycloakHelperService } from "./keycloak.helper.service";

export interface ConfigurationPolicyCompleteDTO {
  configurationPolicy: ConfigurationPolicy;
  uiConfiguration: UIConfigurationDTO;
}

export interface ConfigurationPolicy {
  carWorkshopGroupDTOS: any[];
  criteriaApplicationRadiusKm: number | null;
  description: string | null;
  driverSchedule: boolean;
  driverViewsBudget: boolean;
  fixedPrices: boolean;
  hasBot: boolean;
  hasSingleSignOn: boolean;
  hasSupportChannelPhone: boolean;
  hasWinchPhone: boolean;
  id: number;
  name: string;
  supportChannelPhone: string;
  uiConfigurationOption: any | null;
  winchPhone: string;
  workshopDisplayCriteria: string;
  uiConfiguration: UIConfigurationDTO;
}

export interface UIConfigurationDTO {
  id?: number;
  configurationPolicyId?: number;
  highlightColor: string;
  loadingImage: string;
  logoImage: string;
  title: string;
  senderEmail: string;
  supportEmail: string;
}

@Injectable({ providedIn: "root" })
export class AppConfigService {
  private readonly endpoint = `${environment.apiUrl}configuration-policy/client`;

  private defaultConfig: ConfigurationPolicyCompleteDTO = {
    configurationPolicy: null,
    uiConfiguration: {
      logoImage: "assets/img/icons-driveb/MARCA-drive-b-fleet-principal.png",
      loadingImage: "assets/img/favicon/favicon.png",
      highlightColor: "#007bc0",
      title: "DriveB Fleet",
      senderEmail: "donotreply@bosch-frotas.com.br",
      supportEmail: "suporte.driveb@br.bosch.com"
    }
  };

  private config$ = new BehaviorSubject<ConfigurationPolicyCompleteDTO>(
    this.defaultConfig
  );

  constructor(
    private http: HttpClient,
    private keycloakHelperService: KeycloakHelperService
  ) {}

  private base64ToObjectUrl(base64: string, type = "image/png"): string {
    if (!base64) return "";

    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type });
    return URL.createObjectURL(blob);
  }

  findClientConfiguration() {
    if (
      !this.keycloakHelperService.isFleetAdminLogged() &&
      !this.keycloakHelperService.isDriverLogged()
    ) {
      this.applyConfig(this.defaultConfig);
      return of(this.defaultConfig);
    }

    return this.http.get<any>(this.endpoint).pipe(
      map((response) => {
        let configPolicy: ConfigurationPolicyCompleteDTO = response;

        if (response.uiConfiguration) {
          configPolicy.uiConfiguration.logoImage = this.base64ToObjectUrl(
            response.uiConfiguration.logoImage
          );

          configPolicy.uiConfiguration.loadingImage = this.base64ToObjectUrl(
            response.uiConfiguration.loadingImage
          );
        } else {
          configPolicy.uiConfiguration = this.defaultConfig.uiConfiguration;
        }

        this.applyConfig(configPolicy);
        return configPolicy.uiConfiguration;
      }),
      catchError((err) => {
        this.applyConfig(this.defaultConfig);
        return of(this.defaultConfig);
      })
    );
  }

  private applyConfig(config: ConfigurationPolicyCompleteDTO) {
    this.config$.next(config);

    document.documentElement.style.setProperty(
      "--primary-color",
      config.uiConfiguration?.highlightColor
    );

    this.updateFavicon(config.uiConfiguration?.loadingImage);

    document.title = config.uiConfiguration?.title || "DriveB Fleet";
  }

  getConfig$() {
    return this.config$.asObservable();
  }

  get currentConfig() {
    return this.config$.value;
  }

  private updateFavicon(iconUrl: string) {
    let link: HTMLLinkElement | null =
      document.querySelector("link[rel~='icon']") ||
      document.createElement("link");

    link.type = "image/png";
    link.rel = "icon";
    link.href = iconUrl;

    if (!link.parentElement) {
      document.head.appendChild(link);
    }
  }
}
